import React, { FunctionComponent, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import OrderPaymentItem from './order-payment-item/order-payment-item.component';

import brand from 'assets/styles/variables/brand';
import fonts from 'assets/styles/variables/fonts';
import Button from 'components/button/button.component';
import { useReduxDispatch } from 'helpers/use-redux-dispatch.helper';
import { RootState } from 'modules/core/state/root.reducer';
import { getOrderPayments } from 'modules/order/order.slice';

interface IComponentProps {
	orderId: string;
}

const StyledWrapper = styled.div`
	display: flex;
	flex-direction: column;
`;

const StyledButtons = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	justify-content: space-between;

	&:before {
		content: '';
		display: block;
		width: 100%;
		height: 1px;
		background: ${brand.borders};
		margin: 20px 0;
	}
`;

const StyledHeadings = styled.div`
	display: flex;
	align-items: center;
	font-size: ${fonts.sizes.med};
	line-height: ${fonts.line_height.large};
	justify-content: space-between;
	user-select: none;
	padding: 0 0 0 15px;
`;

const StyledHeading = styled.div`
	display: flex;
	align-items: center;
	text-align: right;
	width: 100%;
	max-width: 130px;
	font-weight: ${fonts.weights.medium};

	cursor: pointer;

	:focus {
		outline: none;
	}

	&:first-child {
		max-width: 270px;
	}

	&:nth-child(4),
	&:nth-child(5) {
		justify-content: center;
		max-width: 80px;
	}

	&:last-of-type {
		max-width: 86px;
	}
`;

const StyledPaymentsList = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
`;

// order payments form wrapper
const OrderPayments: FunctionComponent<IComponentProps> = ({ orderId }) => {
	// Get redux dispatch
	const dispatch = useReduxDispatch();
	// Get history
	const history = useHistory();
	// Get order from store
	const transactions = useSelector(
		(state: RootState) => state.order.activeOrderTransactions!
	);

	// dispatch to get order payment details
	useEffect(() => {
		const getData = async (id: string) => {
			await dispatch(getOrderPayments(id));
		};
		getData(orderId!);
	}, [dispatch, orderId]);

	return (
		<StyledWrapper>
			<StyledHeadings>
				<StyledHeading>
					<FormattedMessage id="orderPayments.heading.id" />
				</StyledHeading>
				<StyledHeading>
					<FormattedMessage id="orderPayments.heading.date" />
				</StyledHeading>
				<StyledHeading>
					<FormattedMessage id="orderPayments.heading.status" />
				</StyledHeading>
				<StyledHeading>
					<FormattedMessage id="orderPayments.heading.reversed" />
				</StyledHeading>
				<StyledHeading>
					<FormattedMessage id="orderPayments.heading.refunded" />
				</StyledHeading>
				<StyledHeading>
					<FormattedMessage id="orderPayments.heading.amount" />
				</StyledHeading>
			</StyledHeadings>
			<StyledPaymentsList aria-label="payments-list">
				{transactions?.length > 0 ? (
					transactions.map((item) => (
						<OrderPaymentItem item={item} key={item.id} />
					))
				) : (
					<FormattedMessage id="orderPayments.noTransactions" />
				)}
			</StyledPaymentsList>
			<StyledButtons>
				<Button
					variant="secondary"
					onClick={() => history.push('/orders')}
				>
					<FormattedMessage id="form.button.back" />
				</Button>
			</StyledButtons>
		</StyledWrapper>
	);
};

export default OrderPayments;
